import {Vue} from 'vue-property-decorator';
import {cloneDeep, get, isEqual, set} from 'lodash';
import {HTTP_SERVICE_INSTANCE} from '@/services/http.service';
import {URL_SERVICE_INSTANCE} from '@/services/url.service';
import {AxiosResponse} from 'axios';
import {AmetrasAdvancedFieldState, FieldStateExecutor} from './field-state-executor';
import {StaticContextField} from './static-context-field';
import {TempParameter} from './temp-parameter';
import {
    ComponentListener,
    ExtendedDynamicComponent,
    ExtendedDynamicComponentList
} from './conditional-components.interface';
// @ts-ignore
import {AmetrasAdvancedButtonState, ButtonStateExecutor} from './button-state-executor';
import {CompositeKeyType} from '@/interfaces/key.interface';
import {GLOBAL_EVENT_LISTENER_SERVICE_INSTANCE} from '@/services/global-event-listener.service';
import {finalize, map, of, Subject, switchMap} from 'rxjs';
import {DisguiseBusinessExceptionWrapper} from '@/exception/disguise-business-exception-wrapper';

export class AdvancedFieldFunctionExecutor {
    private readonly advancedFieldFunctionComposition: DisguiseDto.AdvancedFieldFunctionComposition;
    private readonly fieldRefExecutors: FieldStateExecutor[] = [];
    private readonly buttonRefExecutors: ButtonStateExecutor[] = [];
    private readonly staticContextFields: StaticContextField[] = [];
    private readonly conditionalComponentDefinition: ExtendedDynamicComponentList;
    private readonly tempParameter: { [uniqueTempParameterName: string]: TempParameter } = {};
    private readonly contextParamUpdateCb: (patchableContextParams: CompositeKeyType) => void;
    private readonly nativeConditionalComponentEventCb: (
        nativeActionConfiguration: DisguiseDto.INativeEventConfigurationUnion,
        eventData: any
    ) => void;
    private readonly nativeErrorHandlingCb: (errorResponse: AxiosResponse) => void;
    private readonly increasePendingRequestCountCb: () => void;
    private readonly decreasePendingRequestCountCb: () => void;

    constructor(advancedFieldFunctionComposition: DisguiseDto.AdvancedFieldFunctionComposition,
                conditionalComponentDefinition: ExtendedDynamicComponentList,
                tempParameter: { [uniqueTempParameterName: string]: TempParameter },
                contextParamUpdateCb: (patchableContextParams: CompositeKeyType) => void,
                nativeConditionalComponentEventCb: (
                    nativeActionConfiguration: DisguiseDto.INativeEventConfigurationUnion,
                    eventData: any
                ) => void,
                nativeErrorHandlingCb: (errorResponse: AxiosResponse) => void,
                increasePendingRequestCountCb: () => void,
                decreasePendingRequestCountCb: () => void,
    ) {
        this.advancedFieldFunctionComposition = advancedFieldFunctionComposition;
        this.conditionalComponentDefinition = conditionalComponentDefinition;
        this.tempParameter = tempParameter;
        this.contextParamUpdateCb = contextParamUpdateCb;
        this.nativeConditionalComponentEventCb = nativeConditionalComponentEventCb;
        this.nativeErrorHandlingCb = nativeErrorHandlingCb;
        this.increasePendingRequestCountCb = increasePendingRequestCountCb;
        this.decreasePendingRequestCountCb = decreasePendingRequestCountCb;
    }

    /**
     * Binds the given field dynamically to the Field function executor
     * @param fieldRef                          the reference to the vue field.
     * @param currentFieldReferenceDescription  the api configuration
     * @param fieldState                        the reference to the model value. Attention: must be an object and not
     *                                          a plain string. Otherwise we lose reference on update.
     * @param focusNextFieldReference           (optional) reference to the next field to be focussed
     * @param onFieldEvent                      passthrough of all events
     */
    bindNewFieldStateExecutor(
        fieldRef: Vue,
        currentFieldReferenceDescription: DisguiseDto.FieldReferenceDescription,
        fieldState: AmetrasAdvancedFieldState,
        onFieldEvent: Subject<{ eventName: string, eventArgs: any }>
    ) {
        this.fieldRefExecutors.push(
            new FieldStateExecutor(fieldRef,
                currentFieldReferenceDescription,
                fieldState,
                () => {
                    return this.triggerEvent(this.tempParameter);
                },
                this.tempParameter,
                onFieldEvent
            )
        );
    }

    /**
     * Binds the given field dynamically to the Field function executor
     * @param fieldRef                          the reference to the vue field.
     * @param currentFieldReferenceDescription  the api configuration
     * @param buttonState                       the button state
     * @param onButtonEvent                     (optional) custom passthrough of all events
     */
    bindNewButtonStateExecutor(
        fieldRef: Vue,
        currentFieldReferenceDescription: DisguiseDto.AdvancedButtonReferenceDescription,
        buttonState: AmetrasAdvancedButtonState,
        onButtonEvent?: Subject<{ eventName: string, eventArgs: any }>
    ) {
        this.buttonRefExecutors.push(
            new ButtonStateExecutor(
                fieldRef,
                currentFieldReferenceDescription,
                buttonState,
                () => {
                    return this.triggerEvent(this.tempParameter);
                },
                onButtonEvent
            )
        );
    }

    /**
     * Binds the given field dynamically to the Field function executor
     * @param currentFieldReferenceDescription  the api configuration
     * @param staticValue                       the reference to the static value. Attention: must be an object and not
     *                                          a plain string. Otherwise we lose reference on update.
     */
    bindNewStaticContextField(currentFieldReferenceDescription: DisguiseDto.ContextFieldReferenceDescription,
                              staticValue: CompositeKeyType) {
        this.staticContextFields.push(
            new StaticContextField(
                currentFieldReferenceDescription,
                staticValue
            ));
    }

    /**
     * Simplest variant of a remote call
     * Performs GET/POST Request
     *
     * Passes the result to the handleResult method.
     */
    triggerEvent(tempParameter: { [uniqueTempParameterName: string]: TempParameter }): Promise<unknown> {
        return new Promise<unknown>((resolve, reject) => {
            const params: { [key: string]: string | object } = {};

            for (const currentRefExecutor of this.fieldRefExecutors) {
                // is there any watch option configured that excludes the current execution?
                if (currentRefExecutor.isOperationExcludedInAnyWatchOption()) {
                    this.removeAllLocalErrors();
                    // stop any further execution!
                    return;
                }

                const currentKeyValuePair = currentRefExecutor.collectKeyValuePair();
                if (currentKeyValuePair.key) {
                    set(params, currentKeyValuePair.key, currentKeyValuePair.value);
                }
            }

            for (const currentStaticContextField of this.staticContextFields) {
                const currentKeyValuePair = currentStaticContextField.collectKeyValuePair();
                if (currentKeyValuePair.key) {
                    set(params, currentKeyValuePair.key, currentKeyValuePair.value);
                }
            }
            for (const currentTempParameterUsageDescription
                of this.advancedFieldFunctionComposition.tempParameterUsageDescriptions || []) {
                const tempParameterDescription = tempParameter[
                currentTempParameterUsageDescription.uniqueTempParameterName || ''
                    ];
                if (tempParameterDescription) {
                    set(params,
                        currentTempParameterUsageDescription.endpointKeyPath || '',
                        tempParameterDescription.getModelValue()
                    );
                }
            }

            const axios = HTTP_SERVICE_INSTANCE.getAxios();
            if (this.advancedFieldFunctionComposition.endpointMethod === 'GET') {
                let endpointGetUrl = this.advancedFieldFunctionComposition.endpointUrl || '';
                if (endpointGetUrl?.indexOf('?') > 0) {
                    endpointGetUrl += '&';
                } else {
                    endpointGetUrl += '?';
                }
                this.wrapRequest(
                    axios.get(endpointGetUrl + URL_SERVICE_INSTANCE.serialize(params)),
                    (response) => {
                        this.handleResult(response, tempParameter);
                        resolve(response);
                    },
                    (error) => {
                        if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
                            this.handleApiError(error.response);
                        }
                        reject(error);
                    }
                );
            } else if (this.advancedFieldFunctionComposition.endpointMethod === 'DELETE') {
                let endpointGetUrl = this.advancedFieldFunctionComposition.endpointUrl || '';
                if (endpointGetUrl?.indexOf('?') > 0) {
                    endpointGetUrl += '&';
                } else {
                    endpointGetUrl += '?';
                }
                this.wrapRequest(
                    axios.delete(endpointGetUrl + URL_SERVICE_INSTANCE.serialize(params)),
                    (response) => {
                        this.handleResult(response, tempParameter);
                        resolve(response);
                    },
                    (error) => {
                        if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
                            this.handleApiError(error.response);
                        }
                        reject(error);
                    }
                );
            } else if (this.advancedFieldFunctionComposition.endpointMethod === 'GET_NEW_TAB') {
                let endpointGetUrl = this.advancedFieldFunctionComposition.endpointUrl || '';
                if (endpointGetUrl?.indexOf('?') > 0) {
                    endpointGetUrl += '&';
                } else {
                    endpointGetUrl += '?';
                }
                window.open(endpointGetUrl + URL_SERVICE_INSTANCE.serialize(params), '_blank');
                resolve(undefined);
            } else if (this.advancedFieldFunctionComposition.endpointMethod === 'HEAD') {
                let endpointGetUrl = this.advancedFieldFunctionComposition.endpointUrl || '';
                if (endpointGetUrl?.indexOf('?') > 0) {
                    endpointGetUrl += '&';
                } else {
                    endpointGetUrl += '?';
                }
                this.wrapRequest(
                    axios.head(endpointGetUrl + URL_SERVICE_INSTANCE.serialize(params)),
                    (response) => {
                        this.handleResult(response, tempParameter);
                        resolve(response);
                    },
                    (error) => {
                        if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
                            this.handleApiError(error.response);
                        }
                        reject(error);
                    }
                );
            } else if (this.advancedFieldFunctionComposition.endpointMethod === 'POST') {
                this.wrapRequest(
                    axios.post(this.advancedFieldFunctionComposition.endpointUrl || '', params),
                    (response) => {
                        this.handleResult(response, tempParameter);
                        resolve(response);
                    },
                    (error) => {
                        if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
                            this.handleApiError(error.response);
                        }
                        reject(error);
                    }
                );
            } else if (this.advancedFieldFunctionComposition.endpointMethod === 'PUT') {
                this.wrapRequest(
                    axios.put(this.advancedFieldFunctionComposition.endpointUrl || '', params),
                    (response) => {
                        this.handleResult(response, tempParameter);
                        resolve(response);
                    },
                    (error) => {
                        if (!(error instanceof DisguiseBusinessExceptionWrapper)) {
                            this.handleApiError(error.response);
                        }
                        reject(error);
                    }
                );
            } else {
                resolve(undefined);
            }

            for (const currentRefExecutor of this.fieldRefExecutors) {
                currentRefExecutor.updateToIntermediateState();
            }

            const intermediateExpressions =
                this.advancedFieldFunctionComposition.intermediateExpressionDefinitions;
            if (intermediateExpressions && intermediateExpressions.expression) {
                // there is a local expression rule set defined
                let newValue;
                if (this.expressionMatches(intermediateExpressions.expression)) {
                    newValue = intermediateExpressions.positiveValue;
                } else {
                    newValue = intermediateExpressions.negativeValue;
                }
                if (newValue !== undefined) {
                    for (const currentRefExecutor of this.fieldRefExecutors) {
                        currentRefExecutor.conditionalUpdateAfterIntermediateExpressionEvaluated(newValue);
                    }
                }
            }

            const intermediateRequiredExpressions =
                this.advancedFieldFunctionComposition.intermediateRequiredExpressionDefinitions;
            if (intermediateRequiredExpressions && intermediateRequiredExpressions.expression) {
                // there is a local expression rule set defined
                let newValue: boolean;
                if (this.expressionMatches(intermediateRequiredExpressions.expression)) {
                    newValue = intermediateRequiredExpressions.positiveValue !== undefined ? intermediateRequiredExpressions.positiveValue : true;
                } else {
                    newValue = intermediateRequiredExpressions.negativeValue !== undefined ? intermediateRequiredExpressions.negativeValue : false;
                }
                if (newValue !== undefined) {
                    for (const currentRefExecutor of this.fieldRefExecutors) {
                        currentRefExecutor.conditionalUpdateAfterIntermediateRequiredExpressionEvaluated(newValue, intermediateRequiredExpressions.reasonKey || '');
                    }
                }
            }

            const tempParameterUpdateDescriptions =
                this.advancedFieldFunctionComposition.tempParameterUpdateDescriptions;
            for (const currentTempParamUpdateDescr of tempParameterUpdateDescriptions || []) {
                const uniqueTempParameterName = currentTempParamUpdateDescr.uniqueTempParameterName;
                const intermediateValueByFieldReference =
                    currentTempParamUpdateDescr.intermediateValueByFieldReference;
                if (intermediateValueByFieldReference !== undefined && uniqueTempParameterName !== undefined) {
                    const fieldRef = this.lookupFieldRefExecutorByFieldReference(intermediateValueByFieldReference);
                    if (!fieldRef) {
                        throw new Error('Field reference not found.');
                    }
                    const tempParam = tempParameter[uniqueTempParameterName];
                    if (!tempParam) {
                        throw new Error('Temp param not found. ' + uniqueTempParameterName);
                    }
                    tempParam.handleIntermediateUpdateByFieldValue(
                        currentTempParamUpdateDescr,
                        fieldRef.getModelValue()
                    );
                }
            }

            for (const originalIntermediateComponent of this.advancedFieldFunctionComposition.intermediateComponents || []) {
                if (originalIntermediateComponent.component !== undefined) {
                    const primaryKey: CompositeKeyType = {};
                    if (this.staticContextFields) {
                        for (const currentStaticContextField of this.staticContextFields || []) {
                            const currentKeyValuePair = currentStaticContextField.collectKeyValuePair();
                            primaryKey[currentKeyValuePair.key] = currentKeyValuePair.value;
                        }
                        for (const currentTempParameterUsageDescription
                            of (originalIntermediateComponent.component || {}).tempParameterUsageDescriptions || []) {
                            for (const currentConditionalComponentTempParameterUsageDescription
                                of currentTempParameterUsageDescription.tempParameterUsageDescriptions || []) {
                                const targetTempParameter =
                                    tempParameter
                                        [currentConditionalComponentTempParameterUsageDescription
                                        .uniqueTempParameterName || ''];
                                if (!targetTempParameter) {
                                    throw new Error('Temp parameter lookup failed. '
                                        + currentConditionalComponentTempParameterUsageDescription.uniqueTempParameterName);
                                }
                                primaryKey[currentConditionalComponentTempParameterUsageDescription.endpointKeyPath || '']
                                    = targetTempParameter.getModelValue();
                            }
                        }
                    }

                    const currentIntemediateComponent: ExtendedDynamicComponent = {
                        ...originalIntermediateComponent.component,
                        primaryKey
                    };

                    const listener: ComponentListener = {};
                    const conditionalComponentListeners = currentIntemediateComponent.listener || [];
                    for (const currentConditionalComponentListenerDescription of conditionalComponentListeners) {
                        const eventName = currentConditionalComponentListenerDescription.eventName || '';
                        if (eventName === 'selected') {
                            listener[eventName] = (data) => {
                                HTTP_SERVICE_INSTANCE.getAxios().get(data.url)
                                    .then((searchResponse) => {
                                        let responseEntry = searchResponse.data;
                                        if (Array.isArray(responseEntry)) {
                                            responseEntry = responseEntry.length === 1 ? responseEntry[0] : null;
                                        }
                                        searchResponse.data = responseEntry;

                                        if (!currentConditionalComponentListenerDescription.handleSelectedEventProperly) {
                                            // old legacy way
                                            this.handleResult(
                                                searchResponse,
                                                tempParameter
                                            );

                                            const originalConditionalComponentId =
                                                // original component id
                                                currentIntemediateComponent.uniqueComponentId || '';
                                            const conditionalComponent =
                                                this.conditionalComponentDefinition.componentsByIdentifier[
                                                    originalConditionalComponentId
                                                    ];
                                            if (!conditionalComponent) {
                                                throw new Error(`Unable to find conditional component with
                                           id ${originalConditionalComponentId}`);
                                            } else {
                                                const newMap = {
                                                    ...this.conditionalComponentDefinition.componentsByIdentifier
                                                };
                                                delete newMap[originalConditionalComponentId];
                                                this.conditionalComponentDefinition.componentsByIdentifier = newMap;
                                            }
                                        } else {
                                            // better and more general way
                                            this.executeConditionalComponentListener(
                                                eventName,
                                                responseEntry,
                                                currentConditionalComponentListenerDescription,
                                                tempParameter,
                                                this.conditionalComponentDefinition,
                                                currentIntemediateComponent
                                            );
                                        }
                                    });
                            };
                        } else {
                            listener[eventName] = (data) => {
                                this.executeConditionalComponentListener(
                                    eventName,
                                    data,
                                    currentConditionalComponentListenerDescription,
                                    tempParameter,
                                    this.conditionalComponentDefinition,
                                    currentIntemediateComponent
                                );
                            };
                        }
                    }

                    for (const currentConditionalComponentTempParameterUsageDescription
                        of (originalIntermediateComponent.component || {}).tempParameterUsageDescriptions || []) {

                        const currentStringTemplate =
                            currentConditionalComponentTempParameterUsageDescription.stringTemplate;
                        if (!currentStringTemplate
                            || !currentStringTemplate.template
                            || !currentStringTemplate.replacementRegExp) {
                            throw new Error('Missing or invalid string template for Current Conditional Component' +
                                ' Temp Parameter Usage Description');
                        }

                        const separatorSign = currentStringTemplate.separatorSign || '';
                        const optionalParameterPrefix = currentStringTemplate.optionalParameterPrefix;

                        if (currentConditionalComponentTempParameterUsageDescription.updateMode === 'APPEND') {
                            let currentValueToBeAppended = get(
                                currentIntemediateComponent,
                                currentConditionalComponentTempParameterUsageDescription.updatePropertyPath || '');

                            if (optionalParameterPrefix !== undefined
                                && currentValueToBeAppended.indexOf(optionalParameterPrefix) < 0) {
                                currentValueToBeAppended += optionalParameterPrefix;
                            }

                            for (const currentTempParameterUsageDescription of
                            currentConditionalComponentTempParameterUsageDescription.tempParameterUsageDescriptions
                            || []) {
                                const currentTempParameterUsageDescriptionName =
                                    currentTempParameterUsageDescription.uniqueTempParameterName || '';
                                const currentTempParameterForAppending =
                                    tempParameter[currentTempParameterUsageDescriptionName];
                                if (currentTempParameterForAppending === undefined) {
                                    throw new Error('Temp Parameter lookup failed.');
                                }

                                const lastOldValueCharacter = currentValueToBeAppended.substr(
                                    currentValueToBeAppended.length - 1
                                );
                                if (lastOldValueCharacter !== separatorSign) {
                                    currentValueToBeAppended += separatorSign;
                                }

                                currentValueToBeAppended +=
                                    currentStringTemplate.template
                                        .replace('{endpointKeyPath}',
                                            currentTempParameterUsageDescription.endpointKeyPath || '')
                                        .replace('{value}',
                                            currentTempParameterForAppending.getModelValue());
                            }

                            set(currentIntemediateComponent,
                                currentConditionalComponentTempParameterUsageDescription.updatePropertyPath
                                || '', currentValueToBeAppended);
                        } else if (currentConditionalComponentTempParameterUsageDescription.updateMode === 'REPLACE_OR_APPEND') {
                            let currentValueToBeReplacedOrAppended = get(
                                currentIntemediateComponent,
                                currentConditionalComponentTempParameterUsageDescription.updatePropertyPath || ''
                            );

                            if (optionalParameterPrefix !== undefined
                                && currentValueToBeReplacedOrAppended.indexOf(optionalParameterPrefix) < 0) {
                                currentValueToBeReplacedOrAppended += optionalParameterPrefix;
                            }

                            for (const currentTempParameterUsageDescription of
                            currentConditionalComponentTempParameterUsageDescription.tempParameterUsageDescriptions
                            || []) {
                                const currentTempParameterUsageDescriptionName =
                                    currentTempParameterUsageDescription.uniqueTempParameterName || '';
                                const currentTempParameterForReplacingOrAppending =
                                    tempParameter[currentTempParameterUsageDescriptionName];
                                if (currentTempParameterForReplacingOrAppending === undefined) {
                                    throw new Error('Temp Parameter lookup failed.');
                                }

                                const newValueString = currentStringTemplate.template
                                    .replace('{endpointKeyPath}',
                                        currentTempParameterUsageDescription.endpointKeyPath || '')
                                    .replace('{value}',
                                        currentTempParameterForReplacingOrAppending.getModelValue());

                                const regExpSearchString = currentStringTemplate.replacementRegExp
                                    .replace('{endpointKeyPath}',
                                        currentTempParameterUsageDescription.endpointKeyPath || '')
                                    .replace('{value}',
                                        currentTempParameterForReplacingOrAppending.getModelValue());

                                const replacementPattern = new RegExp('(.*)(' + regExpSearchString + ')(.*)');
                                const matches = currentValueToBeReplacedOrAppended.match(replacementPattern);
                                if (matches) {
                                    // param is already present
                                    currentValueToBeReplacedOrAppended = matches[1] + newValueString + matches[3];
                                } else {
                                    // param must be appended
                                    const lastOldValueCharacter = currentValueToBeReplacedOrAppended.substr(
                                        currentValueToBeReplacedOrAppended.length - 1
                                    );
                                    let optionalSeparationSign = '';
                                    if (lastOldValueCharacter !== separatorSign) {
                                        optionalSeparationSign = separatorSign;
                                    }
                                    currentValueToBeReplacedOrAppended =
                                        currentValueToBeReplacedOrAppended + optionalSeparationSign + newValueString;
                                }
                            }
                            set(currentIntemediateComponent,
                                currentConditionalComponentTempParameterUsageDescription.updatePropertyPath || '',
                                currentValueToBeReplacedOrAppended
                            );

                        } else {
                            throw new Error('Unsupported currentTempParameterUsageDescription updateMode');
                        }
                    }

                    for (const currentFieldUsageDescr of originalIntermediateComponent.component.fieldValueUsageDescriptions || []) {

                        const currentStringTemplate =
                            currentFieldUsageDescr.stringTemplate;
                        if (!currentStringTemplate
                            || !currentStringTemplate.template
                            || !currentStringTemplate.replacementRegExp) {
                            throw new Error('Missing or invalid string template for Current Conditional Component' +
                                ' Temp Parameter Usage Description');
                        }

                        const separatorSign = currentStringTemplate.separatorSign || '';
                        const optionalParameterPrefix = currentStringTemplate.optionalParameterPrefix;

                        if (currentFieldUsageDescr.updateMode === 'APPEND') {
                            let currentValueToBeAppended = get(
                                currentIntemediateComponent,
                                currentFieldUsageDescr.updatePropertyPath || '');

                            if (optionalParameterPrefix !== undefined
                                && currentValueToBeAppended.indexOf(optionalParameterPrefix) < 0) {
                                currentValueToBeAppended += optionalParameterPrefix;
                            }

                            const currentFieldReferenceDescr = currentFieldUsageDescr.fieldReferenceDescription;
                            if (currentFieldReferenceDescr !== undefined) {
                                const fieldRef = this.lookupFieldRefExecutorByFieldReference(currentFieldReferenceDescr);
                                if (fieldRef === undefined) {
                                    throw new Error('Field Reference lookup failed.');
                                }

                                const lastOldValueCharacter = currentValueToBeAppended.substr(
                                    currentValueToBeAppended.length - 1
                                );
                                if (lastOldValueCharacter !== separatorSign) {
                                    currentValueToBeAppended += separatorSign;
                                }

                                currentValueToBeAppended +=
                                    currentStringTemplate.template
                                        .replace('{endpointKeyPath}',
                                            currentFieldReferenceDescr.endpointKeyPath || '')
                                        .replace('{value}',
                                            fieldRef.getModelValue());
                            } else {
                                throw new Error('field reference not defined');
                            }

                            set(currentIntemediateComponent,
                                currentFieldUsageDescr.updatePropertyPath
                                || '', currentValueToBeAppended);
                        } else if (currentFieldUsageDescr.updateMode === 'REPLACE_OR_APPEND') {
                            let currentValueToBeReplacedOrAppended = get(
                                currentIntemediateComponent,
                                currentFieldUsageDescr.updatePropertyPath || ''
                            );

                            if (optionalParameterPrefix !== undefined
                                && currentValueToBeReplacedOrAppended.indexOf(optionalParameterPrefix) < 0) {
                                currentValueToBeReplacedOrAppended += optionalParameterPrefix;
                            }

                            const currentFieldReferenceDescr = currentFieldUsageDescr.fieldReferenceDescription;
                            if (currentFieldReferenceDescr !== undefined) {
                                const fieldRef = this.lookupFieldRefExecutorByFieldReference(currentFieldReferenceDescr);
                                if (!fieldRef) {
                                    throw new Error('Field Reference lookup failed.');
                                }

                                let excluded = false;
                                const modelValue = fieldRef.getModelValue();
                                for (const currentExclusionExpr of currentFieldUsageDescr.valueExclusionExpressions || []) {
                                    if (modelValue === currentExclusionExpr) {
                                        excluded = true;
                                        break;
                                    }
                                }
                                let newValueString;
                                if (!excluded) {
                                    newValueString = currentStringTemplate.template
                                        .replace('{endpointKeyPath}',
                                            currentFieldReferenceDescr.endpointKeyPath || '')
                                        .replace('{value}', modelValue);
                                } else {
                                    newValueString = currentStringTemplate.template
                                        .replace('{endpointKeyPath}',
                                            currentFieldReferenceDescr.endpointKeyPath || '')
                                        .replace('{value}', '');
                                }

                                const regExpSearchString = currentStringTemplate.replacementRegExp
                                    .replace('{endpointKeyPath}',
                                        currentFieldReferenceDescr.endpointKeyPath || '')
                                    .replace('{value}', modelValue);

                                const replacementPattern = new RegExp('(.*)(' + regExpSearchString + ')(.*)');
                                const matches = currentValueToBeReplacedOrAppended.match(replacementPattern);
                                if (matches) {
                                    // param is already present
                                    currentValueToBeReplacedOrAppended = matches[1] + newValueString + matches[3];
                                } else {
                                    // param must be appended
                                    const lastOldValueCharacter = currentValueToBeReplacedOrAppended.substr(
                                        currentValueToBeReplacedOrAppended.length - 1
                                    );
                                    let optionalSeparationSign = '';
                                    if (lastOldValueCharacter !== separatorSign) {
                                        optionalSeparationSign = separatorSign;
                                    }
                                    currentValueToBeReplacedOrAppended =
                                        currentValueToBeReplacedOrAppended + optionalSeparationSign + newValueString;
                                }
                            } else {
                                throw new Error('field reference not defined');
                            }
                            set(currentIntemediateComponent,
                                currentFieldUsageDescr.updatePropertyPath || '',
                                currentValueToBeReplacedOrAppended
                            );

                        } else {
                            throw new Error('Unsupported currentTempParameterUsageDescription updateMode');
                        }
                    }
                    this.conditionalComponentDefinition.componentsByIdentifier = {
                        ...this.conditionalComponentDefinition.componentsByIdentifier,
                        [currentIntemediateComponent.uniqueComponentId || '']: {
                            ...currentIntemediateComponent,
                            object: null,
                            on: listener,
                        }
                    };
                }
            }

            this.conditionalComponentDefinition.componentsByIdentifier = {
                ...this.conditionalComponentDefinition.componentsByIdentifier
            };

            const intermediateContextParameterUpdateDescriptions =
                this.advancedFieldFunctionComposition.intermediateContextParameterUpdateDescriptions;
            if (intermediateContextParameterUpdateDescriptions && intermediateContextParameterUpdateDescriptions.length) {
                const patchableContextParams: CompositeKeyType = {};
                for (const currentContextParamUpdateDescription of intermediateContextParameterUpdateDescriptions) {
                    const paramName = currentContextParamUpdateDescription.uniqueContextParameterName || '';
                    let paramValueAtKeyPath;
                    if (currentContextParamUpdateDescription.value !== undefined) {
                        paramValueAtKeyPath = currentContextParamUpdateDescription.value;
                    } else if (currentContextParamUpdateDescription.fieldReference !== undefined) {
                        const fieldRef = this.lookupFieldRefExecutorByFieldReference(
                            currentContextParamUpdateDescription.fieldReference
                        );
                        if (!fieldRef) {
                            throw new Error('Field reference not found.');
                        }
                        paramValueAtKeyPath = fieldRef.getModelValue();
                    }
                    patchableContextParams[paramName] = paramValueAtKeyPath;
                }
                this.contextParamUpdateCb(patchableContextParams);
            }
        });

    }

    afterInit() {
        for (const currentFieldRefExecutor of this.fieldRefExecutors) {
            currentFieldRefExecutor.afterInit();
        }
        for (const currentButtonExecutor of this.buttonRefExecutors) {
            currentButtonExecutor.afterInit();
        }
    }

    afterReInit() {
        for (const currentFieldRefExecutor of this.fieldRefExecutors) {
            currentFieldRefExecutor.afterReInit();
        }
        for (const currentButtonExecutor of this.buttonRefExecutors) {
            currentButtonExecutor.afterReInit();
        }
    }

    /**
     * Destroys the AFFE functions
     */
    destroy() {
        for (const currentFieldRefExecutor of this.fieldRefExecutors) {
            currentFieldRefExecutor.destroy();
        }
        for (const currentButtonExecutor of this.buttonRefExecutors) {
            currentButtonExecutor.destroy();
        }
    }

    /**
     * Inform every field, that there is a new update
     * @param response the raw API Response
     * @param tempParameter all temp parameters
     */
    private handleResult(response: AxiosResponse, tempParameter: { [uniqueTempParameterName: string]: TempParameter }) {

        for (const currentRefExecutor of this.fieldRefExecutors) {
            currentRefExecutor.handleResponse(response.data);
        }
        for (const currentRefExecutor of this.buttonRefExecutors) {
            currentRefExecutor.handleResponse(response.data);
        }

        for (const currentTempParamUpdateDescription
            of this.advancedFieldFunctionComposition.tempParameterUpdateDescriptions || []) {
            const paramName = currentTempParamUpdateDescription.uniqueTempParameterName || '';
            const tempParam = tempParameter[paramName];
            if (!tempParam) {
                throw new Error('Temp param not found. ' + paramName);
            }

            if (!currentTempParamUpdateDescription.intermediateValueByFieldReference) {
                tempParam.handleUpdate(currentTempParamUpdateDescription, response.data);

                if (currentTempParamUpdateDescription.fieldName) {
                    for (const currentRefExecutor of this.fieldRefExecutors) {
                        if (currentRefExecutor.getFieldReferenceDescription().foreignKeyPath
                            === currentTempParamUpdateDescription.fieldName) {
                            tempParam.setModelValue(currentRefExecutor.getModelValue());
                            tempParam.tempParameterDescription.value = currentRefExecutor.getModelValue();
                        }
                    }
                }
            }
        }

        const contextParamUpdateDefs = this.advancedFieldFunctionComposition.contextParameterUpdateDescriptions;
        if (contextParamUpdateDefs && contextParamUpdateDefs.length) {
            const patchableContextParams: CompositeKeyType = {};
            for (const currentContextParamUpdateDescription of contextParamUpdateDefs) {
                const paramName = currentContextParamUpdateDescription.uniqueContextParameterName || '';
                let paramValueAtKeyPath;
                if (currentContextParamUpdateDescription.keyPath !== undefined) {
                    paramValueAtKeyPath = get(response.data, currentContextParamUpdateDescription.keyPath || '');
                } else if (currentContextParamUpdateDescription.value !== undefined) {
                    paramValueAtKeyPath = currentContextParamUpdateDescription.value;
                } else if (currentContextParamUpdateDescription.fieldReference !== undefined) {
                    const fieldRef = this.lookupFieldRefExecutorByFieldReference(
                        currentContextParamUpdateDescription.fieldReference
                    );
                    if (!fieldRef) {
                        throw new Error('Field reference not found.');
                    }
                    paramValueAtKeyPath = fieldRef.getModelValue();
                }
                patchableContextParams[paramName] = paramValueAtKeyPath;
            }
            this.contextParamUpdateCb(patchableContextParams);
        }

        const toBeDestroyedConditionalComponents: ExtendedDynamicComponent[] = [];

        for (const originalConditionalComponent
            of this.advancedFieldFunctionComposition.conditionalComponents || []) {
            if (originalConditionalComponent.component !== undefined) {
                const primaryKey: CompositeKeyType = {};
                if (this.staticContextFields) {
                    for (const currentStatisContextField of this.staticContextFields || []) {
                        const currentKeyValuePair = currentStatisContextField.collectKeyValuePair();
                        primaryKey[currentKeyValuePair.key] = currentKeyValuePair.value;
                    }
                    for (const currentTempParameterUsageDescription
                        of (originalConditionalComponent.component || {}).tempParameterUsageDescriptions || []) {
                        for (const currentConditionalComponentTempParameterUsageDescription
                            of currentTempParameterUsageDescription.tempParameterUsageDescriptions || []) {
                            const targetTempParameter =
                                tempParameter
                                    [currentConditionalComponentTempParameterUsageDescription
                                    .uniqueTempParameterName || ''];
                            if (!targetTempParameter) {
                                throw new Error('Temp parameter lookup failed. '
                                    + currentConditionalComponentTempParameterUsageDescription.uniqueTempParameterName);
                            }
                            primaryKey[currentConditionalComponentTempParameterUsageDescription.endpointKeyPath || '']
                                = targetTempParameter.getModelValue();
                        }
                    }
                }


                const currentConditionalComponent: ExtendedDynamicComponent = {
                    ...cloneDeep(originalConditionalComponent.component),
                    primaryKey
                };

                const valueAtForeignKeyPath = get(response.data, originalConditionalComponent.foreignKeyPath || '');
                const expressionMatches = originalConditionalComponent.foreignKeyValueExpression === undefined ||
                    valueAtForeignKeyPath === originalConditionalComponent.foreignKeyValueExpression;
                if (expressionMatches) {
                    const listener: ComponentListener = {};
                    const conditionalComponentListeners = currentConditionalComponent.listener || [];
                    for (const currentConditionalComponentListenerDescription of conditionalComponentListeners) {
                        const eventName = currentConditionalComponentListenerDescription.eventName || '';
                        if (eventName === 'selected') {
                            listener[eventName] = (data) => {
                                HTTP_SERVICE_INSTANCE.getAxios().get(data.url)
                                    .then((searchResponse) => {
                                        let responseEntry = searchResponse.data;
                                        if (Array.isArray(responseEntry)) {
                                            responseEntry = responseEntry.length === 1 ? responseEntry[0] : null;
                                        }
                                        searchResponse.data = responseEntry;

                                        if (!currentConditionalComponentListenerDescription.handleSelectedEventProperly) {
                                            // old legacy way
                                            this.handleResult(
                                                searchResponse,
                                                tempParameter
                                            );

                                            const originalConditionalComponentId =
                                                // original component id
                                                currentConditionalComponent.uniqueComponentId || '';
                                            const conditionalComponent =
                                                this.conditionalComponentDefinition.componentsByIdentifier[
                                                    originalConditionalComponentId
                                                    ];
                                            if (!conditionalComponent) {
                                                throw new Error(`Unable to find conditional component with
                                           id ${originalConditionalComponentId}`);
                                            } else {
                                                const newMap = {
                                                    ...this.conditionalComponentDefinition.componentsByIdentifier
                                                };
                                                delete newMap[originalConditionalComponentId];
                                                this.conditionalComponentDefinition.componentsByIdentifier = newMap;
                                            }
                                        } else {
                                            // better and more general way
                                            this.executeConditionalComponentListener(
                                                eventName,
                                                responseEntry,
                                                currentConditionalComponentListenerDescription,
                                                tempParameter,
                                                this.conditionalComponentDefinition,
                                                currentConditionalComponent
                                            );
                                        }
                                    });
                            };
                        } else {
                            listener[eventName] = (data) => {
                                this.executeConditionalComponentListener(
                                    eventName,
                                    data,
                                    currentConditionalComponentListenerDescription,
                                    tempParameter,
                                    this.conditionalComponentDefinition,
                                    currentConditionalComponent
                                );
                            };
                        }
                    }

                    for (const currentConditionalComponentTempParameterUsageDescription
                        of (originalConditionalComponent.component || {}).tempParameterUsageDescriptions || []) {

                        const currentStringTemplate =
                            currentConditionalComponentTempParameterUsageDescription.stringTemplate;
                        if (!currentStringTemplate
                            || !currentStringTemplate.template
                            || !currentStringTemplate.replacementRegExp) {
                            throw new Error('Missing or invalid string template for Current Conditional Component' +
                                ' Temp Parameter Usage Description');
                        }

                        const separatorSign = currentStringTemplate.separatorSign || '';
                        const optionalParameterPrefix = currentStringTemplate.optionalParameterPrefix;

                        if (currentConditionalComponentTempParameterUsageDescription.updateMode === 'APPEND') {
                            let currentValueToBeAppended = get(
                                currentConditionalComponent,
                                currentConditionalComponentTempParameterUsageDescription.updatePropertyPath || '');

                            if (optionalParameterPrefix !== undefined
                                && currentValueToBeAppended.indexOf(optionalParameterPrefix) < 0) {
                                currentValueToBeAppended += optionalParameterPrefix;
                            }

                            for (const currentTempParameterUsageDescription of
                            currentConditionalComponentTempParameterUsageDescription.tempParameterUsageDescriptions
                            || []) {
                                const currentTempParameterUsageDescriptionName =
                                    currentTempParameterUsageDescription.uniqueTempParameterName || '';
                                const currentTempParameterForAppending =
                                    tempParameter[currentTempParameterUsageDescriptionName];
                                if (currentTempParameterForAppending === undefined) {
                                    throw new Error('Temp Parameter lookup failed.');
                                }

                                const lastOldValueCharacter = currentValueToBeAppended.substr(
                                    currentValueToBeAppended.length - 1
                                );
                                if (lastOldValueCharacter !== separatorSign) {
                                    currentValueToBeAppended += separatorSign;
                                }

                                currentValueToBeAppended +=
                                    currentStringTemplate.template
                                        .replace('{endpointKeyPath}',
                                            currentTempParameterUsageDescription.endpointKeyPath || '')
                                        .replace('{value}',
                                            currentTempParameterForAppending.getModelValue());
                            }

                            set(currentConditionalComponent,
                                currentConditionalComponentTempParameterUsageDescription.updatePropertyPath
                                || '', currentValueToBeAppended);
                        } else if (currentConditionalComponentTempParameterUsageDescription.updateMode === 'REPLACE_OR_APPEND') {
                            let currentValueToBeReplacedOrAppended = get(
                                currentConditionalComponent,
                                currentConditionalComponentTempParameterUsageDescription.updatePropertyPath || ''
                            );

                            if (optionalParameterPrefix !== undefined
                                && currentValueToBeReplacedOrAppended.indexOf(optionalParameterPrefix) < 0) {
                                currentValueToBeReplacedOrAppended += optionalParameterPrefix;
                            }

                            for (const currentTempParameterUsageDescription of
                            currentConditionalComponentTempParameterUsageDescription.tempParameterUsageDescriptions
                            || []) {
                                const currentTempParameterUsageDescriptionName =
                                    currentTempParameterUsageDescription.uniqueTempParameterName || '';
                                const currentTempParameterForReplacingOrAppending =
                                    tempParameter[currentTempParameterUsageDescriptionName];
                                if (currentTempParameterForReplacingOrAppending === undefined) {
                                    throw new Error('Temp Parameter lookup failed.');
                                }

                                const newValueString = currentStringTemplate.template
                                    .replace('{endpointKeyPath}',
                                        currentTempParameterUsageDescription.endpointKeyPath || '')
                                    .replace('{value}',
                                        currentTempParameterForReplacingOrAppending.getModelValue());

                                const regExpSearchString = currentStringTemplate.replacementRegExp
                                    .replace('{endpointKeyPath}',
                                        currentTempParameterUsageDescription.endpointKeyPath || '')
                                    .replace('{value}',
                                        currentTempParameterForReplacingOrAppending.getModelValue());

                                const replacementPattern = new RegExp('(.*)(' + regExpSearchString + ')(.*)');
                                const matches = currentValueToBeReplacedOrAppended.match(replacementPattern);
                                if (matches) {
                                    // param is already present
                                    currentValueToBeReplacedOrAppended = matches[1] + newValueString + matches[3];
                                } else {
                                    // param must be appended
                                    const lastOldValueCharacter = currentValueToBeReplacedOrAppended.substr(
                                        currentValueToBeReplacedOrAppended.length - 1
                                    );
                                    let optionalSeparationSign = '';
                                    if (lastOldValueCharacter !== separatorSign) {
                                        optionalSeparationSign = separatorSign;
                                    }
                                    currentValueToBeReplacedOrAppended =
                                        currentValueToBeReplacedOrAppended + optionalSeparationSign + newValueString;
                                }
                            }
                            set(currentConditionalComponent,
                                currentConditionalComponentTempParameterUsageDescription.updatePropertyPath || '',
                                currentValueToBeReplacedOrAppended
                            );

                        } else {
                            throw new Error('Unsupported currentTempParameterUsageDescription updateMode');
                        }
                    }

                    for (const currentFieldUsageDescr of originalConditionalComponent.component.fieldValueUsageDescriptions || []) {

                        const currentStringTemplate =
                            currentFieldUsageDescr.stringTemplate;
                        if (!currentStringTemplate
                            || !currentStringTemplate.template
                            || !currentStringTemplate.replacementRegExp) {
                            throw new Error('Missing or invalid string template for Current Conditional Component' +
                                ' Temp Parameter Usage Description');
                        }

                        const separatorSign = currentStringTemplate.separatorSign || '';
                        const optionalParameterPrefix = currentStringTemplate.optionalParameterPrefix;

                        if (currentFieldUsageDescr.updateMode === 'APPEND') {
                            let currentValueToBeAppended = get(
                                currentConditionalComponent,
                                currentFieldUsageDescr.updatePropertyPath || '');

                            if (optionalParameterPrefix !== undefined
                                && currentValueToBeAppended.indexOf(optionalParameterPrefix) < 0) {
                                currentValueToBeAppended += optionalParameterPrefix;
                            }

                            const currentFieldReferenceDescr = currentFieldUsageDescr.fieldReferenceDescription;
                            if (currentFieldReferenceDescr !== undefined) {
                                const fieldRef = this.lookupFieldRefExecutorByFieldReference(currentFieldReferenceDescr);
                                if (fieldRef === undefined) {
                                    throw new Error('Field Reference lookup failed.');
                                }

                                const lastOldValueCharacter = currentValueToBeAppended.substr(
                                    currentValueToBeAppended.length - 1
                                );
                                if (lastOldValueCharacter !== separatorSign) {
                                    currentValueToBeAppended += separatorSign;
                                }

                                currentValueToBeAppended +=
                                    currentStringTemplate.template
                                        .replace('{endpointKeyPath}',
                                            currentFieldReferenceDescr.endpointKeyPath || '')
                                        .replace('{value}',
                                            fieldRef.getModelValue());
                            } else {
                                throw new Error('field reference not defined');
                            }

                            set(currentConditionalComponent,
                                currentFieldUsageDescr.updatePropertyPath
                                || '', currentValueToBeAppended);
                        } else if (currentFieldUsageDescr.updateMode === 'REPLACE_OR_APPEND') {
                            let currentValueToBeReplacedOrAppended = get(
                                currentConditionalComponent,
                                currentFieldUsageDescr.updatePropertyPath || ''
                            );

                            if (optionalParameterPrefix !== undefined
                                && currentValueToBeReplacedOrAppended.indexOf(optionalParameterPrefix) < 0) {
                                currentValueToBeReplacedOrAppended += optionalParameterPrefix;
                            }

                            const currentFieldReferenceDescr = currentFieldUsageDescr.fieldReferenceDescription;
                            if (currentFieldReferenceDescr !== undefined) {
                                const fieldRef = this.lookupFieldRefExecutorByFieldReference(currentFieldReferenceDescr);
                                if (!fieldRef) {
                                    throw new Error('Field Reference lookup failed.');
                                }

                                let excluded = false;
                                const modelValue = fieldRef.getModelValue();
                                for (const currentExclusionExpr of currentFieldUsageDescr.valueExclusionExpressions || []) {
                                    if (modelValue === currentExclusionExpr) {
                                        excluded = true;
                                        break;
                                    }
                                }
                                let newValueString;
                                if (!excluded) {
                                    newValueString = currentStringTemplate.template
                                        .replace('{endpointKeyPath}',
                                            currentFieldReferenceDescr.endpointKeyPath || '')
                                        .replace('{value}', modelValue);
                                } else {
                                    newValueString = currentStringTemplate.template
                                        .replace('{endpointKeyPath}',
                                            currentFieldReferenceDescr.endpointKeyPath || '')
                                        .replace('{value}', '');
                                }

                                const regExpSearchString = currentStringTemplate.replacementRegExp
                                    .replace('{endpointKeyPath}',
                                        currentFieldReferenceDescr.endpointKeyPath || '')
                                    .replace('{value}', modelValue);

                                const replacementPattern = new RegExp('(.*)(' + regExpSearchString + ')(.*)');
                                const matches = currentValueToBeReplacedOrAppended.match(replacementPattern);
                                if (matches) {
                                    // param is already present
                                    currentValueToBeReplacedOrAppended = matches[1] + newValueString + matches[3];
                                } else {
                                    // param must be appended
                                    const lastOldValueCharacter = currentValueToBeReplacedOrAppended.substr(
                                        currentValueToBeReplacedOrAppended.length - 1
                                    );
                                    let optionalSeparationSign = '';
                                    if (lastOldValueCharacter !== separatorSign) {
                                        optionalSeparationSign = separatorSign;
                                    }
                                    currentValueToBeReplacedOrAppended =
                                        currentValueToBeReplacedOrAppended + optionalSeparationSign + newValueString;
                                }
                            } else {
                                throw new Error('field reference not defined');
                            }
                            set(currentConditionalComponent,
                                currentFieldUsageDescr.updatePropertyPath || '',
                                currentValueToBeReplacedOrAppended
                            );

                        } else {
                            throw new Error('Unsupported currentTempParameterUsageDescription updateMode');
                        }
                    }


                    this.conditionalComponentDefinition.componentsByIdentifier = {
                        ...this.conditionalComponentDefinition.componentsByIdentifier,
                        [currentConditionalComponent.uniqueComponentId || '']: {
                            ...currentConditionalComponent,
                            object: response.data,
                            on: listener,
                        }
                    };
                } else if (originalConditionalComponent.destroyComponentIfForeignKeyValueExpressionNotMatches) {
                    toBeDestroyedConditionalComponents.push(currentConditionalComponent);
                }
            }
        }

        const componentsToStay = {
            ...this.conditionalComponentDefinition.componentsByIdentifier
        };
        if (toBeDestroyedConditionalComponents.length > 0) {
            for (const currentToBeDestroyedConditionalComponent of toBeDestroyedConditionalComponents) {
                delete componentsToStay[currentToBeDestroyedConditionalComponent.uniqueComponentId || ''];
            }
        }
        this.conditionalComponentDefinition.componentsByIdentifier = componentsToStay;

        for (const updateDescription of this.advancedFieldFunctionComposition.conditionalComponentUpdateDescriptions || []) {
            const component = this.conditionalComponentDefinition.componentsByIdentifier[updateDescription.uniqueComponentId || ''];
            if (component && updateDescription.keyPath) {
                const updateValue = get(response.data, updateDescription.keyPath || '');
                set(component.object, updateDescription.updateValuePropertyPath || '', updateValue);
            }
        }

        if (this.advancedFieldFunctionComposition.affePropagateGlobalEvents) {
            for (const globalEvent of this.advancedFieldFunctionComposition.affePropagateGlobalEvents) {
                if (globalEvent.statusCode === undefined || response.status === globalEvent.statusCode) {
                    GLOBAL_EVENT_LISTENER_SERVICE_INSTANCE.notifyListener(
                        globalEvent.event || '',
                        globalEvent.globalEventAction
                    );
                }
            }
        }
    }

    private disableLoadingIndications() {
        for (const currentRefExecutor of this.fieldRefExecutors) {
            currentRefExecutor.disableLoadingIndicationIfConfigured();
        }
    }

    private enableLoadingIndications() {
        for (const currentRefExecutor of this.fieldRefExecutors) {
            currentRefExecutor.enableLoadingIndicationIfConfigured();
        }
    }

    private executeConditionalComponentListener(
        eventName: string,
        data: any,
        currentConditionalComponentListenerDescription: DisguiseDto.ComponentListener,
        tempParameter: { [p: string]: TempParameter },
        conditionalComponents: ExtendedDynamicComponentList,
        currentConditionalComponent: ExtendedDynamicComponent
    ) {
        // 1. update temp param
        const tempParameterUpdateDescriptions =
            currentConditionalComponentListenerDescription.tempParameterUpdateDescriptions;
        for (const currentTempParamUpdateDescription of tempParameterUpdateDescriptions || []) {
            const targetTempParameter = tempParameter[
            currentTempParamUpdateDescription.uniqueTempParameterName || ''];
            if (!targetTempParameter) {
                throw new Error('Temp parameter lookup failed. '
                    + currentTempParamUpdateDescription.uniqueTempParameterName);
            }
            targetTempParameter.handleUpdate(
                currentTempParamUpdateDescription,
                data
            );
        }

        // 2. then update fields and others
        for (const currentRefExecutor of this.fieldRefExecutors) {
            currentRefExecutor.handleConditionalComponentResponse(eventName, data);
        }

        const contextParamUpdateDefs =
            currentConditionalComponentListenerDescription.contextParameterUpdateDescriptions;
        if (contextParamUpdateDefs && contextParamUpdateDefs.length) {
            const patchableContextParams: CompositeKeyType = {};
            for (const currentContextParamUpdateDescription of contextParamUpdateDefs) {
                const paramName = currentContextParamUpdateDescription.uniqueContextParameterName || '';
                let paramValueAtKeyPath;
                if (currentContextParamUpdateDescription.keyPath !== undefined) {
                    paramValueAtKeyPath = get(data, currentContextParamUpdateDescription.keyPath || '');
                } else if (currentContextParamUpdateDescription.value !== undefined) {
                    paramValueAtKeyPath = currentContextParamUpdateDescription.value;
                } else if (currentContextParamUpdateDescription.fieldReference !== undefined) {
                    const fieldRef = this.lookupFieldRefExecutorByFieldReference(
                        currentContextParamUpdateDescription.fieldReference
                    );
                    if (!fieldRef) {
                        throw new Error('Field reference not found.');
                    }
                    paramValueAtKeyPath = fieldRef.getModelValue();
                }
                patchableContextParams[paramName] = paramValueAtKeyPath;
            }
            this.contextParamUpdateCb(patchableContextParams);
        }

        const actionConfiguration = currentConditionalComponentListenerDescription.eventConfiguration;
        if (actionConfiguration !== undefined) {
            if (actionConfiguration['@type'] === 'CloseConditionalComponentEvent') {
                const originalConditionalComponentId =
                    actionConfiguration.uniqueConditionalComponentId // custom id
                    || currentConditionalComponent.uniqueComponentId // original component id
                    || '';
                const dynamicComponent = conditionalComponents.componentsByIdentifier[
                    originalConditionalComponentId
                    ];
                if (!dynamicComponent) {
                    throw new Error(`Unable to find dynamic component with
                                     id ${originalConditionalComponentId}`);
                } else {
                    const newMap = {
                        ...conditionalComponents.componentsByIdentifier
                    };
                    delete newMap[originalConditionalComponentId];
                    conditionalComponents.componentsByIdentifier = newMap;
                }
            } else if (actionConfiguration['@type'] === 'NativeParentConditionalComponentEventConfiguration') {
                if (!actionConfiguration.nativeEventConfiguration) {
                    throw new Error('Missing nativeEventConfiguration');
                }

                this.nativeConditionalComponentEventCb(
                    actionConfiguration.nativeEventConfiguration,
                    data
                );

                const optionalCloseConf = actionConfiguration.optionalCloseConfiguration;

                if (optionalCloseConf !== undefined) {
                    // close target conditional component
                    const originalConditionalComponentId =
                        optionalCloseConf.uniqueConditionalComponentId // custom id
                        || currentConditionalComponent.uniqueComponentId // original component id
                        || '';
                    const dynamicComponent = conditionalComponents.componentsByIdentifier[
                        originalConditionalComponentId
                        ];

                    if (!dynamicComponent) {
                        throw new Error(`Unable to find dynamic component with
                                     id ${originalConditionalComponentId}`);
                    } else {
                        const newMap = {
                            ...conditionalComponents.componentsByIdentifier
                        };
                        delete newMap[originalConditionalComponentId];
                        conditionalComponents.componentsByIdentifier = newMap;
                    }
                }
            }
        }
    }

    /**
     * Evaluates a set of expressions recursively
     *
     * @param expression the most upper expression
     */
    private expressionMatches(expression: DisguiseDto.IAdvancedFieldFunctionExpression): boolean {
        switch (expression['@type']) {
            case 'AndExpression': {
                const castedAndExpression = expression as DisguiseDto.AndExpression;
                for (const currentExp of castedAndExpression.and || []) {
                    if (!this.expressionMatches(currentExp)) {
                        return false;
                    }
                }
                break;
            }
            case 'OrExpression': {
                const castedAndExpression = expression as DisguiseDto.OrExpression;
                for (const currentExp of castedAndExpression.or || []) {
                    if (this.expressionMatches(currentExp)) {
                        return true;
                    }
                }
                return false;
                break;
            }
            case 'FieldValueExpression': {
                const castedFieldValueExpression = expression as DisguiseDto.FieldValueExpression;

                if (castedFieldValueExpression.fieldReference) {
                    const fieldRefExecutor = this.lookupFieldRefExecutorByFieldReference(
                        castedFieldValueExpression.fieldReference
                    );
                    if (!fieldRefExecutor) {
                        throw new Error('Field Reference not found');
                    } else {
                        if (!isEqual(castedFieldValueExpression.valueExpression, fieldRefExecutor.getModelValue())) {
                            return false;
                        }
                    }
                }

                break;
            }
            default: {
                throw new Error('Unknown expression type');
            }
        }

        return true;
    }

    /**
     * Finds a field by its reference
     *
     * the solution is not perfect, as it takes O(n) every time a lookup is triggered.
     * @param fieldReference
     */
    private lookupFieldRefExecutorByFieldReference(fieldReference: DisguiseDto.FieldReferenceBasicFunctionsUnion) {
        let foundRefExecutor;
        for (const currentRefExecutor of this.fieldRefExecutors || []) {
            const currentRefExecutorFieldReference = currentRefExecutor.getFieldReferenceDescription();
            if (currentRefExecutorFieldReference['@type'] !== fieldReference['@type']) {
                // we cannot handle mixtures of reference definitions. e.g. a FT Reference must match a FT Reference
                throw new Error('Mixture of different field reference types detected!');
            }

            if (fieldReference['@type'] === 'FTFieldReferenceDescription') {
                const castedFTFieldReferenceDescription =
                    fieldReference as DisguiseDto.FTFieldReferenceDescription;
                const castedRefExecutorFTFieldReferenceDescription =
                    currentRefExecutorFieldReference as DisguiseDto.FTFieldReferenceDescription;

                // ft references are compared by their reference name
                if (castedFTFieldReferenceDescription.referenceName
                    === castedRefExecutorFTFieldReferenceDescription.referenceName) {
                    foundRefExecutor = currentRefExecutor;
                    break;
                }
            } else if (fieldReference['@type'] === 'DTFieldReferenceDescription') {
                const castedFTFieldReferenceDescription =
                    fieldReference as DisguiseDto.DTFieldReferenceDescription;
                const castedRefExecutorFTFieldReferenceDescription =
                    currentRefExecutorFieldReference as DisguiseDto.DTFieldReferenceDescription;

                // dt references are compared by their column key
                if (castedFTFieldReferenceDescription.key
                    === castedRefExecutorFTFieldReferenceDescription.key) {
                    foundRefExecutor = currentRefExecutor;
                    break;
                }
            }
        }
        return foundRefExecutor;
    }

    private handleApiError(errorResponse: AxiosResponse) {
        this.nativeErrorHandlingCb(errorResponse);
        for (const currentRefExecutor of this.fieldRefExecutors) {
            currentRefExecutor.handleApiError(errorResponse.status);
        }
        const componentsToStay = {
            ...this.conditionalComponentDefinition.componentsByIdentifier
        };
        const toBeDestroyedConditionalComponents: ExtendedDynamicComponent[] = [];
        for (const currentConditionalComponent of this.advancedFieldFunctionComposition.conditionalComponents || []) {
            for (const currentDestroyStatusCode of currentConditionalComponent.apiResponseErrorStatusDestroyCodes || []) {
                if (isEqual(currentDestroyStatusCode, errorResponse.status) && currentConditionalComponent.component !== undefined) {
                    toBeDestroyedConditionalComponents.push(currentConditionalComponent.component);
                }
            }
        }
        if (toBeDestroyedConditionalComponents.length > 0) {
            for (const currentToBeDestroyedConditionalComponent of toBeDestroyedConditionalComponents) {
                delete componentsToStay[currentToBeDestroyedConditionalComponent.uniqueComponentId || ''];
            }
        }
        this.conditionalComponentDefinition.componentsByIdentifier = componentsToStay;
    }

    private removeAllLocalErrors() {
        for (const currentRefExecutor of this.fieldRefExecutors) {
            currentRefExecutor.removeAllMyLocalErrors();
        }
    }

    /**
     * this function wraps the main request and adds
     * - a function that is executed before and
     * - a function that is executed after
     * the request.
     * @param promise the async request
     * @param nextHandler the positive callback
     * @param errorHandler the negative callback
     */
    private wrapRequest<T extends AxiosResponse>(
        promise: Promise<T>,
        nextHandler: (response: T) => void,
        errorHandler: (error: any) => void
    ) {
        of(null)
            .pipe(
                map(() => {
                    this.increasePendingRequestCountCb();
                    this.enableLoadingIndications();
                }),
                switchMap(() => promise),
                finalize(() => {
                    this.decreasePendingRequestCountCb();
                    this.disableLoadingIndications();
                })
            ).subscribe({
            next: nextHandler,
            error: errorHandler,
        });
    }
}
